import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { AgGridTable, LoadingSpinner, EmptyState } from 'components/common';
import { fmtNum } from 'helpers/utils';
import StatTile from './StatTile';
import StackedBar from './StackedBar';
import useResizableHeight from 'components/app/crm/reports/project_schedule_order_entry/useResizableHeight';
import ResizeHandle from 'components/app/crm/reports/project_schedule_order_entry/ResizeHandle';
import ObjectiveLogicNote from './ObjectiveLogicNote';
import { pct, ratioTone, fmtCount, fmtDelta, share, RATIO_TONES, SEGMENT_COLORS, EMPTY } from './objectiveFormat';

const NUM_COL = { width: 120, type: 'numericColumn', cellStyle: { textAlign: 'right' } };

const COLUMN_DEFS = [
  {
    headerName: 'Job number', field: 'j_number', width: 145, minWidth: 145, pinned: 'left', filter: 'agTextColumnFilter',
    cellRenderer: (p) => (p.data?.project_id ? (
      <Link to={`/crm/projects/${p.data.project_id}`} target="_blank" rel="noreferrer" className="tw-font-medium tw-text-primary-600 hover:tw-underline">
        {p.value}
      </Link>
    ) : p.value),
  },
  { headerName: 'Quote number', field: 'q_number', width: 135, minWidth: 135, filter: 'agTextColumnFilter' },
  { headerName: 'Project', field: 'project_name', flex: 2, minWidth: 220, maxWidth: 360, filter: 'agTextColumnFilter', tooltipField: 'project_name' },
  { headerName: 'JAF (MT)', field: 'jaf_weight', ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
  { headerName: 'EST (MT)', field: 'est_weight', ...NUM_COL, valueFormatter: (p) => fmtNum(p.value) },
  { headerName: '%',        field: 'variance_percent', width: 90, type: 'numericColumn',
    cellStyle: { textAlign: 'right' }, valueFormatter: (p) => pct(p.value, 1) },
  { headerName: 'Vs (MT)',  field: 'vs_weight',    ...NUM_COL, valueFormatter: (p) => fmtDelta(p.value),
    cellStyle: (p) => ({ textAlign: 'right', color: Number(p.value) > 0 ? '#15803d' : Number(p.value) < 0 ? '#b91c1c' : '#64748b' }) },
  { headerName: 'CSE OE',   field: 'cse_order_entry', width: 150, minWidth: 150,
    valueFormatter: (p) => (Array.isArray(p.value) ? p.value.join(', ') : p.value) },
  {
    headerName: 'Addendum in progress', field: 'working_on_addendum', width: 180, minWidth: 180,
    cellRenderer: (p) => (p.value ? <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-amber-800">In progress</span> : '—'),
    cellStyle: (p) => (p.value ? { backgroundColor: '#fffbeb' } : { color: '#94a3b8' }),
  },
  { headerName: 'Remarks', field: 'jaf_note', flex: 1, minWidth: 220, maxWidth: 420, tooltipField: 'jaf_note' },
];

const MIN_GRID_HEIGHT = 280;
const MAX_GRID_HEIGHT = 760;

/**
 * Obj.1 — "COMPARISION BETWEEN JAF & EST", one row per job.
 *
 * JAF is read from the latest released deal_contracts record for each job
 * building group. A contract covering multiple buildings is counted once;
 * EST remains per-building and is summed.
 */
const JafEstTab = ({ active, objectiveData, objectiveLoading: loading, objectiveError: error, onTotalsChange }) => {
  const data = objectiveData?.jaf_est_summary?.rows || [];
  const summary = objectiveData?.jaf_est_summary?.summary;
  const { height: gridHeight, min, max, startResize, onKeyDown } = useResizableHeight({
    initial: 420, min: MIN_GRID_HEIGHT, max: MAX_GRID_HEIGHT,
  });

  useEffect(() => {
    if (!active || !summary) return;
    onTotalsChange(`${fmtCount(summary.total)} jobs`);
  }, [active, summary, onTotalsChange]);

  const pinnedBottom = useMemo(() => {
    if (!summary) return null;
    return [{
      j_number: 'TOTAL',
      jaf_weight: summary.jaf_weight,
      est_weight: summary.est_weight,
      vs_weight: summary.vs_weight,
      variance_percent: null,
    }];
  }, [summary]);

  if (loading && data.length === 0) return <LoadingSpinner />;
  if (error) return <EmptyState title="Unable to load JAF vs EST" description={error} />;
  if (!summary || summary.total === 0) {
    return <EmptyState title="No jobs in this range" description="Widen the entry-date range or clear the filters." />;
  }

  const weightScale = Math.max(Number(summary.jaf_weight) || 0, Number(summary.est_weight) || 0);
  const overrun = Number(summary.vs_weight) > 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Headline first — the grid is the evidence, not the answer */}
      <div className="tw-flex tw-flex-wrap tw-gap-3">
        <StatTile
          label="Jobs compared"
          value={fmtCount(summary.total)}
          hint="one row per P/J number"
          accent={SEGMENT_COLORS.primary}
        />
        <StatTile
          label="JAF ≥ EST"
          value={pct(summary.jaf_gte_est?.ratio, 1)}
          hint={`${fmtCount(summary.jaf_gte_est?.count)} jobs within contract weight`}
          tone={ratioTone(summary.jaf_gte_est?.ratio)}
          accent={ratioTone(summary.jaf_gte_est?.ratio)}
          bar={summary.jaf_gte_est?.ratio ?? 0}
        />
        <StatTile
          label="JAF < EST"
          value={fmtCount(summary.jaf_lt_est?.count)}
          hint={`${pct(summary.jaf_lt_est?.ratio, 1)} of jobs overrun the contract weight`}
          tone={summary.jaf_lt_est?.count > 0 ? RATIO_TONES.bad : RATIO_TONES.neutral}
          accent={summary.jaf_lt_est?.count > 0 ? SEGMENT_COLORS.delayed : '#cbd5e1'}
        />
        <StatTile
          label="Working on addendum"
          value={fmtCount(summary.working_on_addendum?.count)}
          hint={`${pct(summary.working_on_addendum?.ratio, 1)} · addendum created, not released`}
          tone={RATIO_TONES.warning}
          accent={SEGMENT_COLORS.unknown}
        />
      </div>

      <div className="tw-flex tw-flex-wrap tw-gap-3">
        <div className="tw-min-w-[320px] tw-flex-[2] tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
          <div className="tw-mb-3 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
            <h3 className="tw-mb-0 tw-text-[13px] tw-font-semibold tw-text-slate-700">Total weight</h3>
            <span className="tw-text-xs tw-text-slate-400">
              Vs&nbsp;
              <span className="tw-font-bold tw-tabular-nums" style={{ color: overrun ? RATIO_TONES.bad : RATIO_TONES.good }}>
                {summary.vs_weight === undefined ? EMPTY : `${fmtDelta(summary.vs_weight)} MT`}
              </span>
            </span>
          </div>
          {[
            { key: 'jaf', label: 'JAF', value: summary.jaf_weight, color: SEGMENT_COLORS.primary },
            { key: 'est', label: 'EST', value: summary.est_weight, color: overrun ? SEGMENT_COLORS.delayed : SEGMENT_COLORS.met },
          ].map((bar) => (
            <div key={bar.key} className="tw-mb-2 tw-flex tw-items-center tw-gap-3 last:tw-mb-0">
              <span className="tw-w-8 tw-flex-shrink-0 tw-text-[11px] tw-font-semibold tw-text-slate-500">{bar.label}</span>
              <div className="tw-h-3 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-100">
                <div className="tw-h-full tw-rounded-full" style={{ width: `${share(bar.value, weightScale) * 100}%`, backgroundColor: bar.color }} />
              </div>
              <span className="tw-w-24 tw-flex-shrink-0 tw-text-right tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-700">
                {fmtNum(bar.value)}
              </span>
            </div>
          ))}
          <p className="tw-mb-0 tw-mt-2 tw-text-[11px] tw-text-slate-400">MT, summed over the jobs in scope</p>
        </div>

        <div className="tw-min-w-[300px] tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
          <h3 className="tw-mb-3 tw-text-[13px] tw-font-semibold tw-text-slate-700">Split by outcome</h3>
          <StackedBar
            total={summary.total}
            height={16}
            segments={[
              { key: 'gte', label: 'JAF ≥ EST', value: summary.jaf_gte_est?.count ?? 0, color: SEGMENT_COLORS.met },
              { key: 'lt',  label: 'JAF < EST', value: summary.jaf_lt_est?.count ?? 0,  color: SEGMENT_COLORS.delayed },
            ]}
          />
          <div className="tw-mt-3 tw-border-t tw-border-slate-100 tw-pt-2 tw-text-[11px] tw-text-slate-500">
            Of those, <span className="tw-font-semibold tw-text-amber-700">{fmtCount(summary.working_on_addendum?.count)}</span> job(s)
            have an addendum in progress that may still absorb the difference.
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-col">
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-300 tw-overflow-hidden">
          <AgGridTable
            rowData={data}
            columnDefs={COLUMN_DEFS}
            loading={loading}
            filter
            sort
            pagination={false}
            rowHeight={32}
            headerHeight={38}
            fontSize="12px"
            height={`${gridHeight}px`}
            pinnedBottomRowData={pinnedBottom}
            colDefOverride={{ wrapHeaderText: true, autoHeaderHeight: true }}
            getRowStyle={(params) => (params.node.rowPinned ? {
              backgroundColor: '#eff6ff',
              borderTop: '2px solid #60a5fa',
              color: '#1e3a8a',
              fontWeight: 700,
            } : null)}
          />
        </div>

        <ResizeHandle
          label="Resize JAF vs EST table"
          height={gridHeight}
          min={min}
          max={max}
          onPointerDown={startResize}
          onKeyDown={onKeyDown}
        />
      </div>

      <ObjectiveLogicNote items={[
        'The selected Entry Date range and filters define the jobs in scope. Contract releases are additionally matched by released date within the same selected date range.',
        'One row means one P/J number. EST is summed across all buildings belonging to that job.',
        'JAF uses every released contract release inside the selected range and sums the weights. For example, releases of 500 MT and 650 MT produce 1,150 MT. A release outside the range is excluded.',
        'If one contract covers several buildings, it is counted once for the job to avoid double-counting. Separate contract release records are each counted once.',
        'Vs (MT) = EST minus JAF: positive means EST is higher than released contract weight; negative means EST is lower. JAF ≥ EST and JAF < EST classify jobs using these totals.',
        'Working on addendum is derived from a building with an addendum created but not released; it is not a direct field from the year-end spreadsheet.',
      ]} />
    </div>
  );
};

JafEstTab.propTypes = {
  active:         PropTypes.bool.isRequired,
  objectiveData:  PropTypes.object,
  objectiveLoading: PropTypes.bool,
  objectiveError: PropTypes.string,
  onTotalsChange: PropTypes.func.isRequired,
};

export default JafEstTab;
